import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import {
	Carousel as CarouselPrimitive,
	CarouselContent,
	CarouselItem,
	type CarouselApi,
} from '@/storybook/components/clientComponents/Carousel/Carousel'
import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react';

/**
 * Componente de carrusel reutilizable que permite navegación automática o manual.
 * 
 * - **Autoplay**: Por defecto, el carrusel avanza automáticamente cada 4 segundos.
 * - **Modo controlado**: Si `isControlled` es `true`, el carrusel desactiva el autoplay y espera que el padre controle el cambio de slides.
 * - **Referencia externa**: Se puede acceder a los métodos `nextSlide` y `goToSlide` desde el componente padre.
 * - **Callback de cambio**: Ejecuta `onSlideChange` cuando cambia el slide.
 * 
 * @component
 * @param {Object} props - Propiedades del carrusel.
 * @param {React.ReactNode} props.children - Slides del carrusel.
 * @param {Object} [props.opts] - Opciones adicionales para la configuración del carrusel.
 * @param {Array} [props.plugins] - Plugins adicionales para el carrusel.
 * @param {string} [props.className] - Clases CSS adicionales para el contenedor principal.
 * @param {string} [props.contentClassName] - Clases CSS para el contenedor de los slides.
 * @param {string} [props.itemClassName] - Clases CSS para cada item del carrusel.
 * @param {boolean} [props.showDots=true] - Si `true`, muestra los indicadores de posición (dots).
 * @param {string} [props.dotsClassName] - Clases CSS para los indicadores de posición.
 * @param {Function} [props.onSlideChange] - Callback que se ejecuta al cambiar de slide.
 * @param {boolean} [props.isControlled=false] - Si `true`, el carrusel no se mueve automáticamente y espera control externo.
 * 
 * @example
 * // Uso sin control externo (autoplay activo)
 * <Carousel>
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 *   <div>Slide 3</div>
 * </Carousel>
 * 
 * @example
 * // Uso con control externo (autoplay desactivado)
 * const ref = useRef<CarouselRef>(null);
 * 
 * return (
 *   <>
 *     <Carousel ref={ref} isControlled={true}>
 *       <div>Slide 1</div>
 *       <div>Slide 2</div>
 *       <div>Slide 3</div>
 *     </Carousel>
 *     <button onClick={() => ref.current?.nextSlide()}>Siguiente</button>
 *   </>
 * );
 */

interface CarouselSettings {
	align: 'center' | undefined;
	dots?: boolean;
	infinite?: boolean;
	speed?: number;
	arrows?: boolean;
	slidesToShow?: number;
	slidesToScroll?: number;
	autoplay?: boolean;
	autoplaySpeed?: number;
  }

type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselPlugin = UseCarouselParameters[1]

interface CarouselProps {
	children: React.ReactNode
	opts?: CarouselSettings
	plugins?: CarouselPlugin
	className?: string
	contentClassName?: string
	itemClassName?: string
	showDots?: boolean
	showArrows?: boolean
	dotsClassName?: string
	onSlideChange?: (index: number) => void 
	isControlled?: boolean,
}

export interface CarouselRef {
	nextSlide: () => void
	goToSlide: (index: number) => void
}

const Carousel = forwardRef<CarouselRef, CarouselProps>(
	(
		{
			children,
			opts = {},
			plugins = [],
			className = '',
			contentClassName = '',
			itemClassName = '',
			showDots = true,
			dotsClassName = '',
			onSlideChange,
			isControlled = false,
		},
		ref,
	) => {
		const [api, setApi] = useState<CarouselApi | null>(null)
		const [current, setCurrent] = useState(0)
		const [count, setCount] = useState(0)

		useEffect(() => {
			if (!api) return

			setCount(api.scrollSnapList().length)
			setCurrent(api.selectedScrollSnap())

			api.on('select', () => {
				const selectedSnap = api.selectedScrollSnap()
				setCurrent(selectedSnap)
				onSlideChange?.(selectedSnap)
			})
		}, [api, onSlideChange])

		useImperativeHandle(ref, () => ({
			/** Avanza al siguiente slide */
			nextSlide: () => {
				if (api) api.scrollNext()
			},
			/** Va a un slide específico por índice */
			goToSlide: (index: number) => {
				if (api) api.scrollTo(index)
			},
		}))


		return (
			<section className="px-6">
				<CarouselPrimitive
					opts={{ align: 'center', ...opts }}
					plugins={!isControlled ? [Autoplay({ delay: 4000 }), ...plugins] : plugins} 
					className={`w-full ${className}`}
					setApi={setApi}
				>
					<CarouselContent className={contentClassName}>
						{React.Children.map(children, (child, index) => (
							<CarouselItem key={index} className={itemClassName}>
								{React.cloneElement(child as React.ReactElement, {
									isActive: index === current,
								})}
							</CarouselItem>
						))}
					</CarouselContent>
				</CarouselPrimitive>
				{showDots && (
					<div className={`py-8 text-center flex gap-2 justify-center items-center ${dotsClassName}`}>
						{Array.from({ length: count }).map((_, index) => (
							<div
								key={index}
								onClick={() => api?.scrollTo(index)}
								className={`h-3 rounded-lg cursor-pointer ${
									index === current ? 'bg-[#90A4AE] w-8' : 'w-3 bg-[#CFD8DC] rounded-full'
								}`}
							/>
						))}
					</div>
				)}
			</section>
		)
	},
)

export default Carousel
